<template>
  <div class="haf-page">
    <header class="haf-header" :style="{ paddingTop: headerPaddingTop + 'px' }">
      <div class="haf-header-nav">
        <button
          v-if="showBack"
          type="button"
          class="haf-back"
          aria-label="返回"
          @click="goBack"
        >
          <i class="el-icon-arrow-left" />
        </button>
        <h1 class="haf-title">健康测评</h1>
        <button
          v-if="step === 6"
          type="button"
          class="haf-skip-top"
          @click="skipVitals"
        >
          暂不知道
        </button>
        <span v-else class="haf-header-placeholder" aria-hidden="true" />
      </div>
      <p class="haf-sub">完成评测，为你生成专属方案</p>
      <div class="haf-progress">
        <div class="haf-progress-bar" :style="{ width: progressPercent + '%' }" />
      </div>
    </header>

    <main class="haf-main">
      <!-- 1 称呼 -->
      <section v-show="step === 0" class="haf-step">
        <h2 class="haf-q">如何称呼您？</h2>
        <input
          v-model.trim="form.nickname"
          class="haf-input"
          type="text"
          maxlength="16"
          placeholder="请输入昵称"
        />
      </section>

      <!-- 2 性别 -->
      <section v-show="step === 1" class="haf-step">
        <h2 class="haf-q">你的性别是？</h2>
        <p class="haf-desc">生理性别和激素会影响我们身体代谢食物的方式</p>
        <div class="haf-gender">
          <button
            type="button"
            class="haf-gender-item"
            :class="{ active: form.gender === 'female' }"
            @click="selectGender('female')"
          >
            <div class="haf-avatar">
              <img
                class="haf-avatar-img"
                :src="avatarFemale"
                alt="女性"
              />
            </div>
            <span>女性</span>
          </button>
          <button
            type="button"
            class="haf-gender-item"
            :class="{ active: form.gender === 'male' }"
            @click="selectGender('male')"
          >
            <div class="haf-avatar">
              <img
                class="haf-avatar-img"
                :src="avatarMale"
                alt="男性"
              />
            </div>
            <span>男性</span>
          </button>
        </div>
      </section>

      <!-- 3 身高 -->
      <section v-show="step === 2" class="haf-step">
        <h2 class="haf-q">你的身高是？</h2>
        <p class="haf-desc">精准身高数据将用于计算你的BMI</p>
        <div class="haf-height-row">
          <ruler-picker
            v-model="form.height"
            :min="140"
            :max="220"
            :step="1"
            :major-every="10"
            :unit-size="8"
            direction="vertical"
          />
          <div class="haf-height-val">
            <span class="num">{{ form.height }}</span>
            <span class="unit">厘米</span>
          </div>
        </div>
      </section>

      <!-- 4 体重 -->
      <section v-show="step === 3" class="haf-step">
        <h2 class="haf-q">你的体重是？</h2>
        <p class="haf-desc">精准体重数据将用于计算你的BMI</p>
        <div class="haf-weight-block">
          <div class="haf-weight-val">
            <span class="num">{{ form.weight.toFixed(1) }}</span>
            <span class="unit">公斤</span>
          </div>
          <ruler-picker
            class="haf-weight-ruler"
            v-model="form.weight"
            :min="30"
            :max="150"
            :step="0.1"
            :major-every="10"
            :unit-size="12"
            direction="horizontal"
          />
        </div>
        <div class="haf-bmi-card">
          <div class="haf-bmi-head">
            <span>你的BMI</span>
            <span>
              <b>{{ bmiValue }}</b>
              <em :style="{ color: bmiMeta.color }">{{ bmiMeta.label }}</em>
            </span>
          </div>
          <div class="haf-bmi-scale">
            <div class="haf-bmi-bar">
              <i class="seg s1" /><i class="seg s2" /><i class="seg s3" /><i class="seg s4" />
              <div class="haf-bmi-pointer" :style="{ left: bmiPointerLeft }" />
            </div>
            <div class="haf-bmi-labels">
              <span>偏瘦</span><span>正常</span><span>超重</span><span>肥胖</span>
            </div>
          </div>
          <p class="haf-bmi-tip">{{ bmiMeta.tip }}</p>
        </div>
        <p class="haf-note">
          注：BMI 是目前诊断肥胖最广泛使用的方法和指标；BMI(kg/m²) = 体重(kg) / 身高²(m²)
        </p>
      </section>

      <!-- 5 出生日期 -->
      <section v-show="step === 4" class="haf-step">
        <h2 class="haf-q">你的出生日期是？</h2>
        <p class="haf-desc">年龄与基础代谢水平、身体活动水平息息相关</p>
        <div class="haf-datetime-wrap">
          <van-datetime-picker
            v-model="form.birthDate"
            type="date"
            :min-date="minBirthDate"
            :max-date="maxBirthDate"
            :show-toolbar="false"
            :visible-item-count="5"
            :formatter="dateFormatter"
          />
        </div>
        <div class="haf-age-card">
          <div class="haf-age-badge">{{ age }} 岁</div>
          <p>{{ ageTip }}</p>
        </div>
      </section>

      <!-- 6 疾病史 -->
      <section v-show="step === 5" class="haf-step haf-step-disease">
        <h3 class="haf-sec-title">个人疾病</h3>
        <div class="haf-chip-grid">
          <button
            v-for="item in personalDiseaseList"
            :key="'p-' + item.tagCode"
            type="button"
            class="haf-chip"
            :class="{ active: isSelected('personal', item.tagCode) }"
            @click="toggleDisease('personal', item.tagCode)"
          >
            {{ item.tagName }}
          </button>
        </div>
        <h3 class="haf-sec-title">家族疾病史</h3>
        <div class="haf-chip-stack">
          <button
            v-for="item in familyDiseaseList"
            :key="'f-' + item.tagCode"
            type="button"
            class="haf-chip full"
            :class="{ active: isSelected('family', item.tagCode) }"
            @click="toggleDisease('family', item.tagCode)"
          >
            {{ item.tagName }}
          </button>
        </div>
      </section>

      <!-- 7 血压心率 -->
      <section v-show="step === 6" class="haf-step">
        <h3 class="haf-sec-title muted">血压测量</h3>
        <div class="haf-ruler-block">
          <p class="haf-ruler-cap">收缩压 {{ form.systolic }} mmHg</p>
          <ruler-picker
            v-model="form.systolic"
            :min="70"
            :max="200"
            :step="1"
            :major-every="10"
            :unit-size="12"
            direction="horizontal"
          />
        </div>
        <div class="haf-ruler-block">
          <p class="haf-ruler-cap">舒张压 {{ form.diastolic }} mmHg</p>
          <ruler-picker
            v-model="form.diastolic"
            :min="40"
            :max="140"
            :step="1"
            :major-every="10"
            :unit-size="12"
            direction="horizontal"
          />
        </div>
        <h3 class="haf-sec-title muted">心率</h3>
        <div class="haf-ruler-block">
          <p class="haf-ruler-cap">{{ form.heartRate }} 次/分</p>
          <ruler-picker
            v-model="form.heartRate"
            :min="40"
            :max="160"
            :step="1"
            :major-every="10"
            :unit-size="12"
            direction="horizontal"
          />
        </div>
      </section>
    </main>

    <footer class="haf-footer">
      <button type="button" class="haf-btn" @click="onPrimary">
        {{ primaryText }}
      </button>
    </footer>
  </div>
</template>
<script>
import { mapState } from 'pinia'
import { useGlobalStore } from '@/stores/global'
import RulerPicker from './components/RulerPicker.vue'
import { health } from '@/api/activity'
import { getHomeRouteName } from '@/utils/uiVersion'

const TOTAL_STEPS = 7

/** 个人疾病（提交用 tagCode，与评测接口文档一致） */
const PERSONAL_DISEASES = [
  { tagCode: 'COMMON_0001', tagName: '高血压' },
  { tagCode: 'COMMON_0002', tagName: '冠心病' },
  { tagCode: 'COMMON_0007', tagName: '脑卒中' },
  { tagCode: 'COMMON_0006', tagName: '心肌梗死' }
]

/** 家族疾病史（提交用 tagCode） */
const FAMILY_DISEASES = [
  { tagCode: 'FAMILY_0001', tagName: '高血压家族史' },
  { tagCode: 'FAMILY_0007', tagName: '冠心病家族史' },
  { tagCode: 'FAMILY_0004', tagName: '糖尿病家族史' },
  { tagCode: 'FAMILY_0006', tagName: '肥胖症家族史' }
]

/**
 * 健康测评分步填写页
 * 结果页读取 sessionStorage 后调用 /health-evaluation/v1/submit
 */
export default {
  name: 'HealthAssessFlow',
  components: { RulerPicker },
  data() {
    const now = new Date()
    return {
      step: 0,
      minBirthDate: new Date(1940, 0, 1),
      maxBirthDate: now,
      personalDiseaseList: PERSONAL_DISEASES,
      familyDiseaseList: FAMILY_DISEASES,
      form: {
        nickname: '',
        gender: '',
        sex: null,
        height: 160,
        weight: 70,
        birthDate: new Date(1980, 0, 1),
        personalDiseases: [],
        familyHistories: [],
        skipDisease: false,
        systolic: 135,
        diastolic: 90,
        heartRate: 60,
        skipVitals: false
      },
      avatarMale: require('@/assets/mine/8ec41e3ab32a6d5fd8b512b00949249c42d0e6af2d589-8nj1zo_fw1200@2x.png'),
      avatarFemale: require('@/assets/mine/3x.png')
    }
  },
  computed: {
    ...mapState(useGlobalStore, ['statusBarHeight']),
    /**
     * 顶栏上边距：对齐 healthAi/report 的 PageHeader（状态栏高度）
     * @returns {number}
     */
    headerPaddingTop() {
      return Number(this.statusBarHeight || 0)
    },
    /**
     * @returns {number}
     */
    progressPercent() {
      return ((this.step + 1) / TOTAL_STEPS) * 100
    },
    /**
     * @returns {boolean}
     */
    hasDiseaseSelected() {
      return this.form.personalDiseases.length > 0 || this.form.familyHistories.length > 0
    },
    /**
     * @returns {string}
     */
    primaryText() {
      if (this.step === 5) return this.hasDiseaseSelected ? '下一步' : '暂无跳过'
      if (this.step === 6) return '评估结果'
      return '下一步'
    },
    /**
     * @returns {string}
     */
    birthdayStr() {
      const birth = this.form.birthDate
      if (!birth) return ''
      return birth.getFullYear() + '-' + this.pad2(birth.getMonth() + 1) + '-' + this.pad2(birth.getDate())
    },
    /**
     * @returns {number}
     */
    age() {
      const birth = this.form.birthDate
      if (!birth) return 0
      const today = new Date()
      let age = today.getFullYear() - birth.getFullYear()
      const m = today.getMonth() - birth.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1
      return Math.max(0, age)
    },
    /**
     * @returns {string}
     */
    ageTip() {
      const a = this.age
      if (a <= 25) return '青春活力期，身体机能处于巅峰，代谢快，恢复力强。'
      if (a <= 30) return '轻熟维持期，代谢开始缓慢下降，生活压力增大，容易发福（过劳肥）'
      if (a <= 40) return '关键转折期，代谢明显放缓，肌肉流失加速，内脏脂肪容易堆积。'
      if (a <= 50) return '稳健保养期，激素水平变化，骨密度开始下降，需要更精细的健康管理。'
      if (a <= 60) {
        return '活力重塑期，面临退休或生活重心转移，激素水平变化明显（如更年期），骨密度流失加速，肌肉量减少。'
      }
      if (a <= 70) {
        return '从容颐养期，身体机能自然衰退，免疫力相对较弱，慢性病风险增加，需要更精细的健康管理。'
      }
      return '安康长寿期，身体机能进一步衰退，需要更多关怀和照护，健康管理以维持现状、预防意外为主。'
    },
    /**
     * @returns {string}
     */
    bmiValue() {
      const h = this.form.height / 100
      if (!h) return '0.0'
      return (this.form.weight / (h * h)).toFixed(1)
    },
    /**
     * @returns {{ label: string, color: string, tip: string, ratio: number }}
     */
    bmiMeta() {
      const bmi = Number(this.bmiValue)
      if (bmi < 18.5) {
        return {
          label: '偏瘦',
          color: '#8a9691',
          tip: '长期处于该区间可能存在营养不良或免疫力下降的风险。',
          ratio: Math.max(0.05, (bmi / 18.5) * 0.25)
        }
      }
      if (bmi < 24) {
        return {
          label: '正常',
          color: '#00bb82',
          tip: '这是最适合中国人群的体重范围，各项健康风险较低。',
          ratio: 0.25 + ((bmi - 18.5) / (24 - 18.5)) * 0.25
        }
      }
      if (bmi < 28) {
        return {
          label: '超重',
          color: '#f5a623',
          tip: '此时身体负担开始增加，是预防慢性病的重要干预窗口。',
          ratio: 0.5 + ((bmi - 24) / (28 - 24)) * 0.25
        }
      }
      return {
        label: '肥胖',
        color: '#ff6b3d',
        tip: '心血管和代谢疾病风险显著升高，需要积极进行体重管理。',
        ratio: Math.min(0.98, 0.75 + ((bmi - 28) / 12) * 0.25)
      }
    },
    /**
     * @returns {string}
     */
    bmiPointerLeft() {
      return this.bmiMeta.ratio * 100 + '%'
    },
    /**
     * 从登录页进入时不展示返回（避免退回登录）
     * @returns {boolean}
     */
    showBack() {
      return String((this.$route.query && this.$route.query.from) || '') !== 'login'
    }
  },
  async mounted() {
    document.title = '健康测评'
    await this.getUserHealthInfo()
  },
  methods: {
    /**
     * 来自健康档案则回档案；否则回新版首页
     * @returns {void}
     */
    goBack() {
      if (this.$route.query.from === 'healthArchive') {
        this.$router.replace({ name: 'healthArchive' }).catch(() => {})
        return
      }
      this.$router.replace({ name: getHomeRouteName() }).catch(() => {})
    },
    /**
     * @param {number} n
     * @returns {string}
     */
    pad2(n) {
      return n < 10 ? '0' + n : String(n)
    },
    /**
     * @param {string} type
     * @param {string} val
     * @returns {string}
     */
    dateFormatter(type, val) {
      if (type === 'year') return val + '年'
      if (type === 'month') return val + '月'
      if (type === 'day') return val + '日'
      return val
    },
    /**
     * @param {'male'|'female'} gender
     * @returns {void}
     */
    selectGender(gender) {
      this.form.gender = gender
      this.form.sex = gender === 'male' ? 1 : 2
    },
    /**
     * @returns {Promise<void>}
     */
    async getUserHealthInfo() {
      try {
        const res = await health()
        if (!(res.code == 1000 || res.code === '1000') || !res.data) return
        const data = res.data
        if (data.name) this.form.nickname = String(data.name).slice(0, 16)
        if (data.sex === 1 || data.sex === '1') {
          this.form.gender = 'male'
          this.form.sex = 1
        } else if (data.sex === 2 || data.sex === '2') {
          this.form.gender = 'female'
          this.form.sex = 2
        }
        if (data.height != null && data.height !== '') {
          this.form.height = Number(data.height) || this.form.height
        }
        if (data.weight != null && data.weight !== '') {
          this.form.weight = Number(data.weight) || this.form.weight
        }
        if (data.birthday) {
          const parts = String(data.birthday).split(/[-/]/)
          if (parts.length >= 3) {
            this.form.birthDate = new Date(
              Number(parts[0]),
              Number(parts[1]) - 1,
              Number(parts[2])
            )
          }
        }
      } catch (e) {
        console.error('获取用户健康信息失败:', e)
      }
    },
    /**
     * @param {'personal'|'family'} type
     * @param {string} tagCode
     * @returns {boolean}
     */
    isSelected(type, tagCode) {
      const list = type === 'personal' ? this.form.personalDiseases : this.form.familyHistories
      return list.indexOf(tagCode) >= 0
    },
    /**
     * @param {'personal'|'family'} type
     * @param {string} tagCode
     * @returns {void}
     */
    toggleDisease(type, tagCode) {
      const key = type === 'personal' ? 'personalDiseases' : 'familyHistories'
      const list = this.form[key]
      const idx = list.indexOf(tagCode)
      if (idx >= 0) list.splice(idx, 1)
      else list.push(tagCode)
      this.form.skipDisease = !this.hasDiseaseSelected
    },
    /**
     * @returns {boolean}
     */
    validateStep() {
      if (this.step === 0 && !this.form.nickname) {
        if (this.$toast) this.$toast('请输入昵称')
        return false
      }
      if (this.step === 0 && this.form.nickname.length > 16) {
        if (this.$toast) this.$toast('称呼长度为1~16个字')
        return false
      }
      if (this.step === 1 && !this.form.sex) {
        if (this.$toast) this.$toast('请选择性别')
        return false
      }
      return true
    },
    /**
     * @returns {void}
     */
    onPrimary() {
      if (this.step === 5) {
        if (!this.hasDiseaseSelected) {
          this.form.personalDiseases = []
          this.form.familyHistories = []
          this.form.skipDisease = true
        } else {
          this.form.skipDisease = false
        }
        this.step += 1
        return
      }
      if (this.step === 6) {
        this.form.skipVitals = false
        this.goResult()
        return
      }
      if (!this.validateStep()) return
      this.step += 1
    },
    /**
     * @returns {void}
     */
    skipVitals() {
      this.form.skipVitals = true
      this.goResult()
    },
    /**
     * 组装 submit 入参写入 sessionStorage，进入结果页提交
     * @returns {void}
     */
    goResult() {
      const payload = {
        nickname: String(this.form.nickname || '').trim(),
        sex: this.form.sex,
        height: parseInt(this.form.height, 10),
        weight: Number(Number(this.form.weight).toFixed(1)),
        birthday: this.birthdayStr,
        skipDisease: !!this.form.skipDisease,
        skipVitals: !!this.form.skipVitals
      }
      if (!payload.skipDisease) {
        payload.personalDiseases = this.form.personalDiseases.slice()
        payload.familyHistories = this.form.familyHistories.slice()
      }
      if (!payload.skipVitals) {
        payload.systolic = this.form.systolic
        payload.diastolic = this.form.diastolic
        payload.heartRate = this.form.heartRate
      }
      try {
        sessionStorage.setItem('healthAssessFlowForm', JSON.stringify(payload))
      } catch (e) {
        /* ignore */
      }
      const query = {}
      if (this.$route.query.from) {
        query.from = this.$route.query.from
      }
      this.$router.push({ name: 'healthAssessResultShowcase', query })
    }
  }
}
</script>

<style scoped>
.haf-page {
  min-height: 100vh;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 20px 24px;
  color: #1a1a1a;
}
.haf-header {
  text-align: center;
  position: relative;
  flex-shrink: 0;
}
.haf-header-nav {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 0 2px;
}
.haf-back {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  color: #00c275;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
}
.haf-back i {
  font-size: 16px;
  font-weight: 700;
}
.haf-title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  max-width: calc(100% - 120px);
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  pointer-events: none;
}
.haf-header-placeholder {
  width: 36px;
  height: 1px;
  flex-shrink: 0;
}
.haf-sub {
  margin: 10px 0 0;
  font-size: 13px;
  color: #9aa3a0;
}
.haf-skip-top {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  min-width: 36px;
  border: none;
  background: transparent;
  color: #9aa3a0;
  font-size: 13px;
  padding: 0;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.haf-progress {
  margin-top: 16px;
  height: 6px;
  border-radius: 99px;
  background: #eef1ef;
  overflow: hidden;
}
.haf-progress-bar {
  height: 100%;
  border-radius: 99px;
  background: #00bb82;
  transition: width 0.25s ease;
}
.haf-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding-top: 36px;
}
.haf-step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.haf-q {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  text-align: center;
}
.haf-desc {
  margin: 10px 0 0;
  font-size: 13px;
  color: #9aa3a0;
  text-align: center;
  line-height: 1.5;
  padding: 0 12px;
}
.haf-input {
  margin-top: 48px;
  width: 100%;
  height: 52px;
  border: none;
  outline: none;
  border-radius: 26px;
  background: #f2f2f2;
  text-align: center;
  font-size: 16px;
  color: #1a1a1a;
  box-sizing: border-box;
  padding: 0 20px;
}
.haf-gender {
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: center;
}
.haf-gender-item {
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: #1a1a1a;
}
.haf-gender-item.active .haf-avatar {
  box-shadow: 0 0 0 3px #00bb82;
}
.haf-avatar {
  width: 112px;
  height: 112px;
  border-radius: 50%;
  overflow: hidden;
  background: #e8f8f1;
}
.haf-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.haf-height-row {
  margin-top: 36px;
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  justify-content: center;
}
.haf-height-val .num {
  font-size: 36px;
  font-weight: 700;
}
.haf-height-val .unit {
  margin-left: 6px;
  font-size: 16px;
  color: #666;
}
.haf-weight-block {
  margin-top: 28px;
  width: 100%;
}
.haf-weight-val {
  margin: 0 0 16px;
  text-align: center;
}
.haf-weight-val .num {
  font-size: 36px;
  font-weight: 700;
}
.haf-weight-val .unit {
  margin-left: 6px;
  font-size: 16px;
  color: #666;
}
.haf-weight-ruler {
  width: 100%;
}
.haf-bmi-card {
  margin-top: 24px;
  width: 100%;
  border: 1px solid #e8eeea;
  border-radius: 14px;
  padding: 16px;
  box-sizing: border-box;
}
.haf-bmi-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #666;
}
.haf-bmi-head b {
  font-size: 18px;
  color: #1a1a1a;
  margin-right: 6px;
}
.haf-bmi-head em {
  font-style: normal;
  font-weight: 600;
}
.haf-bmi-scale {
  margin-top: 16px;
}
.haf-bmi-bar {
  position: relative;
  height: 8px;
  border-radius: 99px;
  display: flex;
  overflow: visible;
}
.haf-bmi-bar .seg {
  flex: 1;
  height: 100%;
}
.haf-bmi-bar .s1 { background: #d7dde0; border-radius: 99px 0 0 99px; }
.haf-bmi-bar .s2 { background: #00bb82; }
.haf-bmi-bar .s3 { background: #f5c242; }
.haf-bmi-bar .s4 { background: #ff8a4c; border-radius: 0 99px 99px 0; }
.haf-bmi-pointer {
  position: absolute;
  top: -8px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 8px solid #00bb82;
  transform: translateX(-50%);
}
.haf-bmi-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-size: 12px;
  color: #9aa3a0;
}
.haf-bmi-tip {
  margin: 14px 0 0;
  font-size: 13px;
  color: #666;
  line-height: 1.55;
}
.haf-note {
  margin-top: 16px;
  font-size: 11px;
  color: #9aa3a0;
  line-height: 1.5;
  text-align: left;
  width: 100%;
}
.haf-datetime-wrap {
  margin-top: 20px;
  width: 100%;
}
.haf-datetime-wrap >>> .van-picker {
  background: transparent;
}
.haf-datetime-wrap >>> .van-picker__mask {
  background-image: linear-gradient(180deg, hsla(0, 0%, 100%, 0.95), hsla(0, 0%, 100%, 0.4)),
    linear-gradient(0deg, hsla(0, 0%, 100%, 0.95), hsla(0, 0%, 100%, 0.4));
}
.haf-datetime-wrap >>> .van-picker-column__item--selected {
  color: #1a1a1a;
  font-weight: 700;
}
.haf-age-card {
  margin-top: 24px;
  width: 100%;
  border: 1px solid #e8eeea;
  border-radius: 14px;
  padding: 28px 16px 18px;
  text-align: center;
  position: relative;
  box-sizing: border-box;
}
.haf-age-badge {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  background: #00bb82;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 8px;
}
.haf-age-card p {
  margin: 8px 0 0;
  font-size: 13px;
  color: #666;
  line-height: 1.55;
}
.haf-step-disease {
  align-items: stretch;
  width: 100%;
}
.haf-sec-title {
  margin: 8px 0 16px;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}
.haf-sec-title.muted {
  color: #9aa3a0;
  font-size: 15px;
  font-weight: 600;
}
.haf-chip-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 28px;
}
.haf-chip-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.haf-chip {
  height: 44px;
  border-radius: 99px;
  border: 1px solid #00bb82;
  background: #fff;
  color: #1a1a1a;
  font-size: 15px;
}
.haf-chip.full {
  width: 100%;
}
.haf-chip.active {
  background: rgba(0, 187, 130, 0.12);
  font-weight: 600;
}
.haf-empty-tip {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  color: #9aa3a0;
}
.haf-ruler-block {
  width: 100%;
  margin-bottom: 18px;
}
.haf-ruler-cap {
  margin: 0 0 8px;
  text-align: center;
  font-size: 13px;
  color: #666;
}
.haf-footer {
  padding-top: 12px;
}
.haf-btn {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: #00bb82;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/views/healthAssessFlow/index.vue